import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type NamirialSigningModuleSpecsFragment } from '../../../../api/fragments/NamirialSigningModuleSpecs';
import {
    UpdateNamirialSettingsDocument,
    type UpdateNamirialSettingsMutation,
    type UpdateNamirialSettingsMutationVariables,
} from '../../../../api/mutations/updateNamirialSettings';
import { type NamirialSettingsInput } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import InputField from '../../../../components/fields/InputField';
import PasswordField from '../../../../components/fields/PasswordField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type UpdateNamirialFormProps = {
    module: NamirialSigningModuleSpecsFragment;
    onComplete: () => void;
};

type FormValues = NamirialSettingsInput;

const validator = validators.compose(validators.requiredString('endpoint'), validators.requiredString('apiToken'));

const UpdateNamirialForm = ({ module, onComplete }: UpdateNamirialFormProps) => {
    const { t } = useTranslation('namirialSigningModuleDetails');

    const initialValues = useMemo(
        () => pick(['endpoint', 'apiToken'], module.namirialSetting),
        [module.namirialSetting]
    );

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (settings: FormValues) => {
            message.loading({
                content: t('namirialSigningModuleDetails:updateNamirialDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateNamirialSettingsMutation, UpdateNamirialSettingsMutationVariables>({
                    mutation: UpdateNamirialSettingsDocument,
                    variables: { namirialModuleId: module.id, settings },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('namirialSigningModuleDetails:updateNamirialDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [onComplete, t, apolloClient, module]
    );

    const validate = useValidator(validator);

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="updateNamirial" name="updateNamirial" onSubmitCapture={handleSubmit}>
                    <InputField
                        {...t('namirialSigningModuleDetails:updateNamirialDrawer.fields.endPoint', {
                            returnObjects: true,
                        })}
                        name="endpoint"
                        required
                    />
                    <PasswordField
                        {...t('namirialSigningModuleDetails:updateNamirialDrawer.fields.apiToken', {
                            returnObjects: true,
                        })}
                        name="apiToken"
                        placeholder="******"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default UpdateNamirialForm;
